/**
 * Tab registry for the ONE type-aware audition/competition editor
 * (organizer management UI redesign, see
 * docs/wip/active/WIP_redesigning_organizer_management_ui_2026_06_04.md).
 *
 * The editor (`audition-form.tsx`) renders ONE form over ONE
 * `UpdateAuditionSchema` + `updateAuditionAction` write path for both record
 * types (Critical Rule #8 — single edit surface). The only structural fork
 * between an audition and a competition is WHICH tabs render — that fork
 * lives here, as data, so it is unit-testable and never duplicated.
 *
 * This registry is metadata-only (id, label key, visibility): the form maps
 * tab ids to components itself, because tab components are not uniform
 * (most take the shared `form` instance; the Dates tab takes the `audition`
 * row). Label keys resolve through the `auditions` namespace with the
 * record-type `context` from `lib/record-terminology.ts`, so wording forks
 * (Applications → Entries) without duplicate registry entries.
 *
 * Tab sets (the editor consumes this in redesign Phase 2a):
 * - audition:    basic, requirements, dates, contract, applications
 *                (labeled "Application settings")
 * - competition: basic (Basic info — also carries branding, public-page
 *                switches, and livestream, folded in from the former
 *                Public Page & Branding tab), requirements (labeled
 *                Eligibility), dates, applications (labeled "Entry settings" —
 *                distinct from the primary-nav "Entries" list workspace),
 *                competitionNetworking (Networking & Features)
 *
 * `contract` is audition-only: competitions have no employment contract.
 * The branding / public-page / livestream fields (former standalone Competition
 * Settings page) live inside the Basic info tab for competitions.
 */
import type { RecordType } from '../../lib/record-terminology';

export interface AuditionFormTabDefinition {
  /** Stable tab id — also the Tabs value and the component-map key. */
  id:
    | 'basic'
    | 'requirements'
    | 'dates'
    | 'contract'
    | 'applications'
    | 'competitionNetworking';
  /**
   * i18n key (in the `auditions` namespace) for the tab trigger label.
   * Resolved with the record-type context, so a single key serves both
   * record types and forks only where a `_competition` twin exists.
   */
  labelKey: string;
  /** Which record types render this tab. */
  visibleFor: readonly RecordType[];
}

export const AUDITION_FORM_TABS: readonly AuditionFormTabDefinition[] = [
  {
    id: 'basic',
    labelKey: 'auditions:editor.tabs.basic',
    visibleFor: ['audition', 'competition'],
  },
  {
    id: 'requirements',
    labelKey: 'auditions:editor.tabs.requirements',
    visibleFor: ['audition', 'competition'],
  },
  {
    id: 'dates',
    labelKey: 'auditions:editor.tabs.dates',
    visibleFor: ['audition', 'competition'],
  },
  {
    id: 'contract',
    labelKey: 'auditions:editor.tabs.contract',
    visibleFor: ['audition'],
  },
  {
    id: 'applications',
    // The editor sub-section is application *settings* (fee, dates,
    // invitation-only) — NOT the entries *list* workspace (the primary record
    // nav's "Entries"/Applications item, `manage.nav.applications`). They are
    // distinct surfaces that previously shared the "Entries"/"Applications"
    // label; the editor section uses its own `applicationsSettings` key so no
    // two on-screen labels collide. (BAL-386)
    labelKey: 'auditions:editor.tabs.applicationsSettings',
    visibleFor: ['audition', 'competition'],
  },
  {
    id: 'competitionNetworking',
    labelKey: 'auditions:editor.tabs.competitionNetworking',
    visibleFor: ['competition'],
  },
] as const;

/** The ordered tab set the editor renders for a record type. */
export function getVisibleTabs(
  type: RecordType,
): readonly AuditionFormTabDefinition[] {
  return AUDITION_FORM_TABS.filter((tab) => tab.visibleFor.includes(type));
}
